/**
 * Dynamic per-host PWA manifest.
 *
 * Every white-label subdomain (e.g. irdo.pharmatrialconnect.health,
 * nga.pharmatrialconnect.africa, phc.pharmatrialconnect.com) is the same SPA
 * bundle, so a static manifest gives every installed home-screen icon the
 * same generic label. This util rewrites the manifest at runtime so each
 * install shows a distinct site identifier under the icon.
 *
 * Pure presentation — no business logic, no network calls.
 */
import {
  buildIconSpec,
  siteTypeFromTld,
  type PwaIconSpec,
  type PwaSiteType,
} from "./pwaIconGenerator";

/**
 * Bump MANIFEST_VERSION whenever name/short_name/icons/start_url/theme
 * change. The version is appended as a query string to the manifest URL so
 * browsers (and installed PWAs that revalidate the manifest) fetch a fresh
 * copy instead of serving a stale cached one. The blob URL used for
 * per-host manifests is regenerated each load, so this only affects the
 * static manifest path used by root domains and by the underlying fetch.
 */
export const MANIFEST_VERSION = "7";
const BASE_MANIFEST_PATH = "/manifest-v5.webmanifest";
const BASE_MANIFEST_URL = `${BASE_MANIFEST_PATH}?v=${MANIFEST_VERSION}`;
export const ROOT_HOSTS = new Set([
  "pharmatrialconnect.com",
  "pharmatrialconnect.africa",
  "pharmatrialconnect.health",
  "www.pharmatrialconnect.com",
  "www.pharmatrialconnect.africa",
  "www.pharmatrialconnect.health",
  "trial-connect-suite.lovable.app",
]);

interface SiteLabel {
  name: string;       // full name in manifest ("name")
  shortName: string;  // home-screen label, ≤12 chars ideal ("short_name")
}

export function deriveLabel(hostname: string): SiteLabel | null {
  const host = hostname.toLowerCase();
  if (ROOT_HOSTS.has(host)) return null; // use static manifest as-is

  // Skip Lovable preview / sandbox hosts
  if (host.includes("lovableproject.com") || host.includes("lovable.app") && !ROOT_HOSTS.has(host)) {
    return null;
  }

  const parts = host.split(".");
  if (parts.length < 3) return null;

  const sub = parts[0];
  const tld = parts[parts.length - 1];

  // Skip www
  if (sub === "www") return null;

  const subUpper = sub.toUpperCase();
  // Short label: "IRDO.health" / "NGA.africa" / "PHC.com" — distinguishes
  // the same slug across different TLDs while staying within ~12 chars.
  const shortName = `${subUpper}.${tld}`.slice(0, 12);
  const name = `${subUpper} · PharmaTrial Connect`;
  return { name, shortName };
}

/**
 * Apply just the <meta> tags (apple-mobile-web-app-title, application-name)
 * for the current hostname. Safe to call repeatedly — idempotent.
 * Extracted so SPA route changes can re-assert the install label without
 * re-fetching the manifest.
 */
export function applySiteLabelMeta(): SiteLabel | null {
  if (typeof document === "undefined") return null;
  const label = deriveLabel(window.location.hostname);
  if (!label) return null;

  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-title"]'
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "apple-mobile-web-app-title";
    document.head.appendChild(meta);
  }
  if (meta.content !== label.shortName) meta.content = label.shortName;

  let appName = document.querySelector<HTMLMetaElement>(
    'meta[name="application-name"]'
  );
  if (!appName) {
    appName = document.createElement("meta");
    appName.name = "application-name";
    document.head.appendChild(appName);
  }
  if (appName.content !== label.shortName) appName.content = label.shortName;

  return label;
}

let currentLabel: SiteLabel | null = null;

function ensureManifestLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  return link;
}

function setHeadLink(rel: string, href: string, sizes?: string) {
  const selector = sizes ? `link[rel="${rel}"][sizes="${sizes}"]` : `link[rel="${rel}"]`;
  let link = document.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    if (sizes) link.setAttribute("sizes", sizes);
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

function setThemeColor(color: string) {
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((m) => {
      m.content = color;
    });
}

/**
 * Public HTTPS URL of the hosted per-host PWA asset (manifest or PNG icon).
 * Phones fetch the manifest/icons from the network at install time, so
 * in-memory blob/data URLs never reach the home screen.
 */
export function pwaAssetUrl(
  kind: "manifest" | "icon",
  hostname: string,
  spec: PwaIconSpec,
  size?: number,
): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  let hex = spec.background.replace("#", "").toLowerCase();
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const params = new URLSearchParams({
    type: kind,
    host: hostname.toLowerCase(),
    site: spec.siteType,
    color: hex,
    v: MANIFEST_VERSION,
  });
  if (size) params.set("size", String(size));
  return `${base}/functions/v1/pwa-asset?${params.toString()}`;
}

/** Points manifest, apple-touch-icon and theme colour at hosted assets. */
function applyIconSpec(spec: PwaIconSpec, _label: SiteLabel) {
  const host = window.location.hostname;
  setThemeColor(spec.background);
  setHeadLink("apple-touch-icon", pwaAssetUrl("icon", host, spec, 180), "180x180");
  const href = pwaAssetUrl("manifest", host, spec);
  const link = ensureManifestLink();
  if (link.href !== href) link.href = href;
}

/** Boot-stage spec from hostname only (slug + TLD). */
export function bootIconSpec(hostname: string): PwaIconSpec | null {
  if (!deriveLabel(hostname)) return null;
  const parts = hostname.toLowerCase().split(".");
  return buildIconSpec({
    slug: parts[0],
    siteType: siteTypeFromTld(parts[parts.length - 1]),
  });
}

/**
 * Stage 2: called by SiteContext once the organization resolves, so the
 * icon uses the real site type and brand colour.
 */
export function updateDynamicPwaBranding(site: {
  slug: string;
  type: PwaSiteType;
  primary_color?: string | null;
}): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const label = currentLabel ?? deriveLabel(window.location.hostname);
  if (!label) return;
  applyIconSpec(
    buildIconSpec({ slug: site.slug, siteType: site.type, primaryColor: site.primary_color }),
    label,
  );
}

export function installDynamicManifest(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const label = applySiteLabelMeta();
  currentLabel = label;

  if (!label) {
    const link = ensureManifestLink();
    if (link.getAttribute("href") !== BASE_MANIFEST_URL) {
      link.href = BASE_MANIFEST_URL;
    }
    return;
  }

  const spec = bootIconSpec(window.location.hostname);
  if (spec) applyIconSpec(spec, label);
}
